import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLanguage } from '../LanguageContext';
import {
  commissionsList, commissionSetStatus, commissionUploadReceipt,
  commissionDeleteReceipt, commissionReceiptBlobUrl,
} from '../api';

// Admin/manager ledger of every broker commission (a closed deal with a broker)
// with a payment status and an optional uploaded receipt as proof of payment.
export default function AdminCommissions() {
  const { lang, isRTL } = useLanguage();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [broker, setBroker] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(null); // dealId currently mutating
  const fileInput = useRef(null);
  const pendingUpload = useRef(null); // dealId awaiting a file pick

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    const res = await commissionsList();
    if (res.error) { setData(null); setFailed(true); } else { setData(res); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = useMemo(() => data?.commissions || [], [data]);
  const brokers = useMemo(() => [...new Set(all.map(c => c.broker))].sort(), [all]);
  const rows = useMemo(() => all.filter(c =>
    (!broker || c.broker === broker) && (!status || c.status === status)
  ), [all, broker, status]);
  const totals = useMemo(() => rows.reduce((a, c) => {
    a.commission += c.amount;
    if (c.status === 'received') a.received += c.amount; else a.pending += c.amount;
    return a;
  }, { commission: 0, received: 0, pending: 0 }), [rows]);

  const money = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

  const toggleStatus = async (c) => {
    setBusy(c.dealId);
    const next = c.status === 'received' ? 'pending' : 'received';
    const res = await commissionSetStatus(c.dealId, next, c.note || '');
    if (!res.error) await load();
    setBusy(null);
  };
  const pickReceipt = (dealId) => { pendingUpload.current = dealId; fileInput.current?.click(); };
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    const dealId = pendingUpload.current;
    e.target.value = '';
    if (!file || dealId == null) return;
    setBusy(dealId);
    const res = await commissionUploadReceipt(dealId, file);
    if (res.error) alert(t('تعذّر رفع الإيصال', 'Could not upload receipt') + (res.error ? `: ${res.error}` : ''));
    else await load();
    setBusy(null);
  };
  const viewReceipt = async (dealId, download) => {
    try {
      const url = await commissionReceiptBlobUrl(dealId);
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = `receipt-${dealId}`; document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, '_blank', 'noopener');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert(t('تعذّر فتح الإيصال', 'Could not open receipt')); }
  };
  const removeReceipt = async (dealId) => {
    if (!window.confirm(t('حذف إيصال هذه العمولة؟', 'Remove this commission receipt?'))) return;
    setBusy(dealId);
    const res = await commissionDeleteReceipt(dealId);
    if (!res.error) await load();
    setBusy(null);
  };

  const kpi = (label, value, color) => (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, flex: '1 1 160px', minWidth: 160 }}>
      <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: color || 'var(--text-primary)' }}>{value}</div>
    </div>
  );
  const cell = { padding: '9px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.84rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const nameCell = { ...cell, textAlign: isRTL ? 'right' : 'left', fontWeight: 600 };
  const selStyle = { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' };
  const statusBadge = (s) => (
    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700,
      background: s === 'received' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
      color: s === 'received' ? '#16a34a' : '#f59e0b' }}>
      {s === 'received' ? t('مستلمة', 'Received') : t('معلّقة', 'Pending')}
    </span>
  );
  const btn = (label, onClick, color) => (
    <button onClick={onClick} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: color || 'var(--text-primary)', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>{label}</button>
  );

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1180, margin: '0 auto' }}>
      <input ref={fileInput} type="file" accept="image/*,application/pdf" onChange={onFilePicked} style={{ display: 'none' }} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={broker} onChange={e => setBroker(e.target.value)} style={selStyle}>
          <option value="">{t('كل الوسطاء', 'All brokers')}</option>
          {brokers.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} style={selStyle}>
          <option value="">{t('كل الحالات', 'All statuses')}</option>
          <option value="pending">{t('معلّقة', 'Pending')}</option>
          <option value="received">{t('مستلمة', 'Received')}</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
      ) : failed ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('تعذّر تحميل البيانات', 'Could not load data')}
          <div style={{ marginTop: 12 }}>{btn(t('إعادة المحاولة', 'Retry'), load)}</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {kpi(t('إجمالي العمولات (ر.س)', 'Total commission (SAR)'), money(totals.commission))}
            {kpi(t('مستلمة (ر.س)', 'Received (SAR)'), money(totals.received), 'var(--success, #16a34a)')}
            {kpi(t('معلّقة (ر.س)', 'Pending (SAR)'), money(totals.pending), 'var(--warning, #f59e0b)')}
            {kpi(t('عدد العمولات', 'Commissions'), money(rows.length))}
          </div>

          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
              <thead>
                <tr style={{ background: 'var(--bg-primary)' }}>
                  <th style={nameCell}>{t('الوسيط', 'Broker')}</th>
                  <th style={cell}>{t('المشروع', 'Project')}</th>
                  <th style={cell}>{t('الوحدة', 'Unit')}</th>
                  <th style={cell}>{t('العمولة (ر.س)', 'Commission')}</th>
                  <th style={cell}>{t('التاريخ', 'Date')}</th>
                  <th style={cell}>{t('الحالة', 'Status')}</th>
                  <th style={cell}>{t('الإيصال', 'Receipt')}</th>
                  <th style={cell}>{t('إجراءات', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td style={{ ...cell, textAlign: 'center' }} colSpan={8}>{t('لا توجد عمولات', 'No commissions')}</td></tr>
                ) : rows.map((c) => (
                  <tr key={c.dealId} style={{ opacity: busy === c.dealId ? 0.5 : 1 }}>
                    <td style={nameCell}>{c.broker}</td>
                    <td style={cell}>{c.project || '—'}</td>
                    <td style={cell}>{c.unit || '—'}</td>
                    <td style={{ ...cell, fontWeight: 700 }}>{money(c.amount)}</td>
                    <td style={{ ...cell, color: 'var(--text-secondary)' }}>{fmtDate(c.dealDate)}</td>
                    <td style={cell}>{statusBadge(c.status)}</td>
                    <td style={cell}>
                      {c.hasReceipt ? (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          {btn(t('عرض', 'View'), () => viewReceipt(c.dealId, false), 'var(--accent)')}
                          {btn('⬇', () => viewReceipt(c.dealId, true))}
                        </span>
                      ) : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    </td>
                    <td style={cell}>
                      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {btn(c.hasReceipt ? t('استبدال الإيصال', 'Replace') : t('رفع إيصال', 'Upload'), () => pickReceipt(c.dealId), 'var(--accent)')}
                        {btn(c.status === 'received' ? t('وضع كمعلّقة', 'Mark pending') : t('وضع كمستلمة', 'Mark received'), () => toggleStatus(c), c.status === 'received' ? '#f59e0b' : '#16a34a')}
                        {c.hasReceipt && btn('🗑', () => removeReceipt(c.dealId), 'var(--danger, #dc2626)')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginTop: 10 }}>
            {t('الإيصال دليل على استلام الوسيط لعمولته. الصيغ المقبولة: صورة أو PDF.', 'The receipt is proof the broker received their commission. Accepted: image or PDF.')}
          </div>
        </>
      )}
    </div>
  );
}
